import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import Spinner from 'src/components/atoms/Spinner';
import { SpinnerColor, SpinnerSize } from 'src/components/atoms/Spinner/Spinner';
import { AutoSuggest } from 'src/components/molecules/AutoSuggest';
import useGooglePlacesAutocomplete from 'src/features/address/hooks/useGooglePlacesAutocomplete';
import useCheckOwnerAddressAvailability from 'src/features/owner/hooks/useCheckOwnerAddressAvailability';
import { OwnerField } from 'src/features/owner/ts/interfaces/Owner';
import { EMPTY_VALUE } from 'src/features/property';
import { IValueLabel } from 'src/ts/interfaces';
import formFieldsConfig from './OwnerFormFieldsConfig';
import { AddressAvailabilityIndicator, OwnerAddressInputContainer } from './styles';

interface OwnerAddressInputProps {
  name: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  methods: {
    trigger: any;
    control: any;
    setValue: any;
    setError: any;
    clearErrors: any;
    watch: any;
  }; // React Hook Form methods
  isCreateForm?: boolean;
  defaultValues: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}

const OwnerAddressInput: React.FC<OwnerAddressInputProps> = ({
  name,
  placeholder,
  methods,
  isCreateForm,
  defaultValues
}: OwnerAddressInputProps) => {
  const { trigger, control, setValue, setError, clearErrors, watch } = methods;

  const watchedFormAddress = watch(OwnerField.address);

  // Watching the full address
  const watchedAddress = watch([
    OwnerField.address,
    OwnerField.city,
    OwnerField.state,
    OwnerField.zip
  ]);

  const [searchAddress, setSearchAddress] = useState('');
  const [shouldCheckAddressAvailability, setShouldCheckAddressAvailability] = useState(false);

  const {
    placeSuggestions,
    isLoading: isPlacesLoading,
    formatPlacesSuggestions,
    setSelectedPlace,
    selectedPlace,
    error: placesError
  } = useGooglePlacesAutocomplete({
    inputValue: searchAddress,
    // Add debounce to prevent excessive API calls
    debounceTime: 300
  });

  // Effect to populate address fields when place is selected
  useEffect(() => {
    if (selectedPlace?.address) {
      setValue(OwnerField.address, selectedPlace.address.street, { shouldDirty: true });
      setValue(OwnerField.city, selectedPlace.address.city, { shouldDirty: true });
      setValue(OwnerField.state, selectedPlace.address.state, { shouldDirty: true });
      setValue(OwnerField.zip, selectedPlace.address.zipCode, { shouldDirty: true });
      trigger(OwnerField.address);
      trigger(OwnerField.city);
      trigger(OwnerField.state);
      trigger(OwnerField.zip);
      setShouldCheckAddressAvailability(true);
    }
  }, [selectedPlace, setValue, trigger]);
  const {
    checkAddress,
    isLoading: isAddressCheckLoading,
    isAvailable: isAddressAvailable
  } = useCheckOwnerAddressAvailability();
  useEffect(() => {
    if (shouldCheckAddressAvailability && !isAddressCheckLoading) {
      const [address, city, state, zip] = watchedAddress;
      checkAddress({
        address,
        city,
        state,
        zip
      });
    }
  }, [shouldCheckAddressAvailability, watchedAddress, checkAddress, isAddressCheckLoading]);
  const [addressIndicator, setAddressIndicator] = useState(<></>);

  useEffect(() => {
    if (isAddressCheckLoading) {
      setAddressIndicator(
        <Spinner size={SpinnerSize.small} color={SpinnerColor.primary} centered />
      );
      return;
    }
    if (isAddressAvailable === true) {
      setAddressIndicator(<img src="/assets/images/check.svg" alt="Available" />);
      clearErrors(OwnerField.address);
    } else if (isAddressAvailable === false) {
      setAddressIndicator(<img src="/assets/images/Failed.svg" alt="Not Available" />);
      setError(OwnerField.address, {
        type: 'manual',
        message: 'This address is already linked to an existing Owner'
      });
    }
    setShouldCheckAddressAvailability(false);
  }, [
    isAddressCheckLoading,
    isAddressAvailable,
    setError,
    clearErrors,
    shouldCheckAddressAvailability
  ]);
  return (
    <OwnerAddressInputContainer>
      <Controller
        name={OwnerField.address}
        control={control}
        render={({ field: { name } }) => (
          <AutoSuggest
            name={name}
            data={
              watchedFormAddress && placeSuggestions.length === 0
                ? [
                    {
                      data: watchedFormAddress,
                      value: watchedFormAddress,
                      label: watchedFormAddress
                    } as IValueLabel
                  ]
                : formatPlacesSuggestions(placeSuggestions)
            }
            placeholder={formFieldsConfig.address.placeholder}
            initialLabel={isCreateForm ? EMPTY_VALUE : defaultValues?.address}
            onChange={({ value }) => {
              setSearchAddress(value.toString());
            }}
            onSelect={(item) => {
              setValue(OwnerField.address, item.value);
              setSelectedPlace({ placeId: item?.metadata?.placeId as string });
            }}
            onClear={() => {
              setValue(OwnerField.address, EMPTY_VALUE);
              setValue(OwnerField.city, EMPTY_VALUE);
              setValue(OwnerField.state, EMPTY_VALUE);
              setValue(OwnerField.zip, EMPTY_VALUE);
              setSearchAddress(EMPTY_VALUE);
            }}
            isLoading={isPlacesLoading}
            hasError={placesError !== null}
            config={formFieldsConfig.address.validationSchema}
            setLabelToValue
            doNotValidate
          />
        )}
      />
      <AddressAvailabilityIndicator>{addressIndicator}</AddressAvailabilityIndicator>
    </OwnerAddressInputContainer>
  );
};

export default OwnerAddressInput;
